import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Input from "../../components/Atomos/Inputs/Input";
import Dropdown from "../../components/Atomos/DropDown/Dropdown";
import EventCalendar from "../../components/Atomos/EventCalendar/EventCalendar";
import NewButton from "../../components/Atomos/Buttons/newButton";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import EventCard from "../../components/Moleculas/EventCard/EventCard";
import { IEvent } from "../../interfaces/models/IEvent";
import ChevronRight from "../../components/Atomos/Icons/ChevronRight";
import ChevronDown from "../../components/Atomos/Icons/ChevronDown";
import SortArrow from "../../components/Atomos/Icons/SortArrow";
import PrintIcon from "../../components/Atomos/Icons/PrintIcon";
import { EventTypeEnum, SetupTypeEnum } from "../../Enums/EventEnums";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { UseCountryList } from "../../hooks/Catalogs/useCountry";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { UseGroupList } from "../../hooks/Catalogs/useGroupList";
import { axios } from "../..";
import { isCurrentEvent } from "../../utils/eventTiming";

const EVENTS_PER_PAGE = 10;
const MAX_EMPTY_FETCH_ATTEMPTS = 10;

const EVENT_API_FIELDS = [
  "id",
  "title",
  "eventType",
  "startDate",
  "endDate",
  "location",
  "description",
  "eventHostSponsor",
  "sponsorHost",
  "regionIds",
  "countryIds",
  "fee",
  "address",
  "shortId",
  "bannerLists",
  "status",
  "groupIds",
  "setupType",
  "memberEventsTab",
  "comingSoonDate",
  "comingSoonLocation",
  "comingSoonLocationMessage",
];

function applyTabFilter(events: IEvent[], activeTab: string): IEvent[] {
  const currentDate = Math.floor(Date.now() / 1000);
  return events
    .filter((event) => event.status === "online")
    .filter((event) => {
      if (activeTab === "WSG UPCOMING") {
        return (
          isCurrentEvent(event, currentDate) && event.memberEventsTab !== true
        );
      }
      if (activeTab === "WSG GROUPS") {
        return (
          event.eventHostSponsor === "group" &&
          isCurrentEvent(event, currentDate) &&
          event.memberEventsTab !== true
        );
      }
      if (activeTab === "MEMBER EVENTS") {
        return (
          event.memberEventsTab === true && isCurrentEvent(event, currentDate)
        );
      }
      if (activeTab === "PAST EVENTS") {
        return (
          !isCurrentEvent(event, currentDate) && event.memberEventsTab !== true
        );
      }
      return true;
    });
}

function buildRequestFilter(
  apiFilter: Record<string, unknown>,
  activeTab: string,
): Record<string, unknown> {
  const filter: Record<string, unknown> = { ...apiFilter };

  if (activeTab === "PAST EVENTS") {
    filter.status = { value: "online", comparisonType: "EQUAL" };
    filter.eventTiming = { value: "past" };
    filter.memberEventsTab = {
      value: true,
      comparisonType: "NOT_EQUAL_OR_NULL",
    };
    return filter;
  }

  if (activeTab === "MEMBER EVENTS") {
    filter.status = { value: "online", comparisonType: "EQUAL" };
    filter.memberEventsTab = {
      value: true,
      comparisonType: "EQUAL",
    };
    return filter;
  }

  // WSG UPCOMING / WSG GROUPS (and default): upcoming live events
  filter.status = { value: "online", comparisonType: "EQUAL" };
  filter.eventTiming = { value: "upcoming" };
  filter.memberEventsTab = {
    value: true,
    comparisonType: "NOT_EQUAL_OR_NULL",
  };

  if (activeTab === "WSG GROUPS") {
    filter.eventHostSponsor = {
      value: "group",
      comparisonType: "EQUAL",
    };
  }

  return filter;
}

function buildEventListParams(
  apiFilter: Record<string, unknown>,
  activeTab: string,
  sortOrder: "asc" | "desc",
  pageIndex: number,
  pageSize: number,
  count = false,
): URLSearchParams {
  const params = new URLSearchParams();
  params.append("fields", JSON.stringify(EVENT_API_FIELDS));
  params.append(
    "filter",
    JSON.stringify(buildRequestFilter(apiFilter, activeTab)),
  );
  params.append(
    "sort",
    JSON.stringify({ field: "startDate", order: sortOrder }),
  );
  params.append(
    "pagination",
    JSON.stringify({ pageIndex, pageSize }),
  );
  if (count) {
    params.append("count", "true");
  }
  return params;
}

function sortEventsByDate(
  events: IEvent[],
  sortOrder: "asc" | "desc",
): IEvent[] {
  const missing =
    sortOrder === "asc" ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
  return [...events].sort((a, b) => {
    const aStart = a.startDate ?? missing;
    const bStart = b.startDate ?? missing;
    return sortOrder === "asc" ? aStart - bStart : bStart - aStart;
  });
}

function mergeUniqueEvents(existing: IEvent[], incoming: IEvent[]): IEvent[] {
  const ids = new Set(existing.map((e) => e.id));
  const merged = [...existing];
  for (const event of incoming) {
    if (!ids.has(event.id)) {
      ids.add(event.id);
      merged.push(event);
    }
  }
  return merged;
}

interface EventFilters {
  keyword: string;
  eventType: { id: string; name: string } | null;
  /** Catalog setup kind (e.g. webinar); distinct from delivery `eventType`. */
  setupType: { id: string; name: string } | null;
  year: { id: string; name: string } | null;
  region: { id: string; name: string } | null;
  country: { id: string; name: string } | null;
}

interface EventTab {
  id: string;
  label: string;
  active: boolean;
}

export default function MemberEvents(): React.ReactElement {
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  const scrollContainersRef = useRef<
    Array<{
      element: HTMLElement;
      originalOverflow: string;
      originalHeight: string;
      originalMaxHeight: string;
    }>
  >([]);
  const restoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPrintingRef = useRef<boolean>(false);

  const restoreScrollStyles = () => {
    if (!isPrintingRef.current) {
      return;
    }

    if (restoreTimeoutRef.current) {
      clearTimeout(restoreTimeoutRef.current);
      restoreTimeoutRef.current = null;
    }

    scrollContainersRef.current.forEach(
      ({ element, originalOverflow, originalHeight, originalMaxHeight }) => {
        if (element && element.style) {
          element.style.overflow = originalOverflow || "";
          element.style.height = originalHeight || "";
          element.style.maxHeight = originalMaxHeight || "";
        }
      },
    );

    if (document.body) {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.maxHeight = "";
    }
    if (document.documentElement) {
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
      document.documentElement.style.maxHeight = "";
    }

    scrollContainersRef.current = [];
    isPrintingRef.current = false;
  };

  useEffect(() => {
    const mediaQueryList = window.matchMedia("print");

    const handleMediaChange = (mql: MediaQueryList | MediaQueryListEvent) => {
      if (!mql.matches && isPrintingRef.current) {
        restoreScrollStyles();
      }
    };

    const handleWindowFocus = () => {
      if (isPrintingRef.current) {
        setTimeout(() => {
          restoreScrollStyles();
        }, 50);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && isPrintingRef.current) {
        setTimeout(() => {
          restoreScrollStyles();
        }, 50);
      }
    };

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", handleMediaChange);
    } else {
      (mediaQueryList as any).addListener(handleMediaChange);
    }

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener("change", handleMediaChange);
      } else {
        (mediaQueryList as any).removeListener(handleMediaChange);
      }
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
      }
      restoreScrollStyles();
    };
  }, []);

  const [filters, setFilters] = useState<EventFilters>({
    keyword: "",
    eventType: null,
    setupType: null,
    year: null,
    region: null,
    country: null,
  });

  const [apiFilter, setApiFilter] = useState<Record<string, unknown>>({});
  const [rawEvents, setRawEvents] = useState<IEvent[]>([]);
  const [totalEventCount, setTotalEventCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageIndexRef = useRef(-1);
  const loadingRef = useRef(false);
  const rawEventsRef = useRef<IEvent[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState("WSG UPCOMING");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [resetCalendar, setResetCalendar] = useState(false);

  const fetchRawPage = useCallback(
    async (pageIndex: number): Promise<IEvent[]> => {
      const params = buildEventListParams(
        apiFilter,
        activeTab,
        sortOrder,
        pageIndex,
        EVENTS_PER_PAGE,
      );
      const response = await axios.Get(`/event?${params.toString()}`);
      return (response?.data ?? []) as IEvent[];
    },
    [apiFilter, activeTab, sortOrder],
  );

  const fetchEventCount = useCallback(async (): Promise<number | null> => {
    try {
      const params = buildEventListParams(
        apiFilter,
        activeTab,
        sortOrder,
        0,
        EVENTS_PER_PAGE,
        true,
      );
      const response = await axios.Get(`/event?${params.toString()}`);
      const count = response?.data;
      return typeof count === "number" ? count : null;
    } catch (error) {
      console.error("Error loading event count:", error);
      return null;
    }
  }, [apiFilter, activeTab, sortOrder]);

  const loadNextBatch = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;

      if (reset) {
        setIsLoadingEvents(true);
        pageIndexRef.current = -1;
        setTotalEventCount(null);
        void fetchEventCount().then((count) => {
          if (count !== null) {
            setTotalEventCount(count);
          }
        });
      } else {
        setIsLoadingMore(true);
      }

      try {
        let accumulated = reset ? [] : [...rawEventsRef.current];
        let added = 0;
        let pageIdx = reset ? 0 : pageIndexRef.current + 1;
        let more = true;
        let emptyAttempts = 0;

        while (
          added < EVENTS_PER_PAGE &&
          more &&
          emptyAttempts < MAX_EMPTY_FETCH_ATTEMPTS
        ) {
          const rawPage = await fetchRawPage(pageIdx);
          more = rawPage.length === EVENTS_PER_PAGE;
          const filtered = applyTabFilter(rawPage, activeTab);
          const beforeLen = accumulated.length;
          accumulated = mergeUniqueEvents(accumulated, filtered);
          added += accumulated.length - beforeLen;
          pageIndexRef.current = pageIdx;

          if (rawPage.length === 0) {
            more = false;
            break;
          }

          if (filtered.length === 0 && more) {
            emptyAttempts++;
          } else {
            emptyAttempts = 0;
          }

          pageIdx++;
        }

        setHasMore(more);
        setRawEvents(sortEventsByDate(accumulated, sortOrder));
      } catch (error) {
        console.error("Error loading events:", error);
        if (reset) {
          setRawEvents([]);
          setHasMore(false);
          setTotalEventCount(null);
        }
      } finally {
        loadingRef.current = false;
        setIsLoadingEvents(false);
        setIsLoadingMore(false);
      }
    },
    [activeTab, fetchRawPage, fetchEventCount, sortOrder],
  );

  useEffect(() => {
    rawEventsRef.current = rawEvents;
  }, [rawEvents]);

  useEffect(() => {
    void loadNextBatch(true);
  }, [activeTab, apiFilter, sortOrder, loadNextBatch]);

  const data = useMemo(
    () => sortEventsByDate(rawEvents, sortOrder),
    [rawEvents, sortOrder],
  );

  const totalForDisplay = !hasMore
    ? data.length
    : totalEventCount ?? data.length;

  const { regionData } = UseRegionList();
  const { countryData } = UseCountryList();
  const { firmData } = UseFirmList();
  const { groupData } = UseGroupList();

  const eventTypeOptions = Object.entries(EventTypeEnum).map(([id, name]) => ({
    id,
    name,
  }));

  const currentYear = new Date().getFullYear();

  const yearOptions: { id: string; name: string }[] = [];

  yearOptions.push({
    id: `next_year_${currentYear + 1}`,
    name: (currentYear + 1).toString(),
  });

  yearOptions.push({
    id: `current_year_upcoming_${currentYear}`,
    name: `${currentYear} (upcoming)`,
  });

  yearOptions.push({
    id: `current_year_past_${currentYear}`,
    name: `${currentYear} (past)`,
  });

  const previousYear = currentYear - 1;
  if (previousYear >= 2025) {
    yearOptions.push({
      id: `previous_year_${previousYear}`,
      name: previousYear.toString(),
    });
  }

  const regionOptions =
    regionData?.map((region: any) => ({
      id: region.id,
      name: region.name,
    })) || [];

  const countryOptions =
    countryData?.map((country: any) => ({
      id: country.id,
      name: country.name,
    })) || [];

  const tabs: EventTab[] = [
    {
      id: "wsg-upcoming",
      label: "WSG UPCOMING",
      active: activeTab === "wsg-upcoming",
    },
    {
      id: "wsg-groups",
      label: "WSG GROUPS",
      active: activeTab === "wsg-groups",
    },
    {
      id: "member-events",
      label: "MEMBER EVENTS",
      active: activeTab === "member-events",
    },
    {
      id: "past-events",
      label: "PAST EVENTS",
      active: activeTab === "past-events",
    },
  ];

  const handleFilterChange = (field: keyof EventFilters, value: any) => {
    let normalizedValue = value;
    if (
      value &&
      typeof value === "object" &&
      (value.id === "" || value.id === null || value.id === undefined)
    ) {
      normalizedValue = null;
    } else if (field === "keyword" && (!value || value.trim() === "")) {
      normalizedValue = "";
    }

    setFilters((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
  };

  const handleTabChange = (tabLabel: string) => {
    setActiveTab(tabLabel);

    if (tabLabel === "PAST EVENTS") {
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }

    setFilters({
      keyword: "",
      eventType: null,
      setupType: null,
      year: null,
      region: null,
      country: null,
    });

    setApiFilter({});
    setResetCalendar(true);
  };

  const handleSearch = () => {
    const filterObject: { [key: string]: any } = {};

    if (filters.year && filters.year.id && filters.year.id.trim()) {
      const yearId = filters.year.id;
      const isPastYear =
        yearId.startsWith("current_year_past_") ||
        yearId.startsWith("previous_year_");
      const isUpcomingYear =
        yearId.startsWith("current_year_upcoming_") ||
        yearId.startsWith("next_year_");

      if (isPastYear && activeTab !== "PAST EVENTS") {
        setActiveTab("PAST EVENTS");
        setSortOrder("desc");
      } else if (isUpcomingYear && activeTab === "PAST EVENTS") {
        setActiveTab("WSG UPCOMING");
        setSortOrder("asc");
      }
    }

    if (filters.keyword && filters.keyword.trim()) {
      filterObject.title = {
        field: "title",
        value: filters.keyword,
        comparisonType: "CONTAINS",
      };
    }

    if (
      filters.eventType &&
      filters.eventType.id &&
      filters.eventType.id.trim()
    ) {
      filterObject.eventType = {
        field: "eventType",
        value: filters.eventType.id,
        comparisonType: "EQUAL",
      };
    }

    if (
      filters.setupType &&
      filters.setupType.id &&
      filters.setupType.id.trim()
    ) {
      filterObject.setupType = {
        field: "setupType",
        value: filters.setupType.id,
        comparisonType: "EQUAL",
      };
    }

    if (filters.year && filters.year.id && filters.year.id.trim()) {
      const yearId = filters.year.id;

      if (yearId.startsWith("next_year_")) {
        const nextYear = parseInt(yearId.replace("next_year_", ""));
        const startOfNextYear = Math.floor(
          new Date(nextYear, 0, 1).getTime() / 1000,
        );
        const endOfNextYear = Math.floor(
          new Date(nextYear + 1, 0, 1).getTime() / 1000,
        );
        filterObject.startDate = {
          field: "startDate",
          value: `${startOfNextYear} - ${endOfNextYear}`,
          comparisonType: "BETWEEN_TS",
        };
      } else if (yearId.startsWith("current_year_upcoming_")) {
        const year = parseInt(yearId.replace("current_year_upcoming_", ""));
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const endOfYear = Math.floor(new Date(year + 1, 0, 1).getTime() / 1000);
        filterObject.startDate = {
          field: "startDate",
          value: `${currentTimestamp} - ${endOfYear}`,
          comparisonType: "BETWEEN_TS",
        };
      } else if (yearId.startsWith("current_year_past_")) {
        const year = parseInt(yearId.replace("current_year_past_", ""));
        const startOfYear = Math.floor(new Date(year, 0, 1).getTime() / 1000);
        const currentTimestamp = Math.floor(Date.now() / 1000);
        filterObject.startDate = {
          field: "startDate",
          value: `${startOfYear} - ${currentTimestamp}`,
          comparisonType: "BETWEEN_TS",
        };
      } else if (yearId.startsWith("previous_year_")) {
        const previousYear = parseInt(yearId.replace("previous_year_", ""));
        const startOfPreviousYear = Math.floor(
          new Date(previousYear, 0, 1).getTime() / 1000,
        );
        const endOfPreviousYear = Math.floor(
          new Date(previousYear + 1, 0, 1).getTime() / 1000,
        );
        filterObject.startDate = {
          field: "startDate",
          value: `${startOfPreviousYear} - ${endOfPreviousYear}`,
          comparisonType: "BETWEEN_TS",
        };
      } else {
        filterObject.startDate = {
          field: "startDate",
          value: filters.year.id || "0",
          comparisonType: "EQUAL",
        };
      }
    }

    if (filters.region && filters.region.id && filters.region.id.trim()) {
      filterObject.regionIds = {
        field: "regionIds",
        value: filters.region.id,
        comparisonType: "ARRAY_CONTAINS",
      };
    }

    if (filters.country && filters.country.id && filters.country.id.trim()) {
      filterObject["address.countryName"] = {
        field: "address.countryName",
        value: [filters.country.name],
        comparisonType: "EQUAL",
      };
    }

    setApiFilter(filterObject);
  };

  const handleEventDetails = (event: IEvent) => {
    navigate(`/events/${event.id}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const legacyEtype = params.get("etype");
    const setupRaw =
      params.get("setupType") ||
      (legacyEtype === "webinar" ? "webinar" : null);
    const past = params.get("past") === "true";

    if (past) {
      setActiveTab("PAST EVENTS");
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }

    if (
      setupRaw &&
      Object.prototype.hasOwnProperty.call(SetupTypeEnum, setupRaw)
    ) {
      const label =
        SetupTypeEnum[setupRaw as keyof typeof SetupTypeEnum] ?? setupRaw;
      setFilters((prev) => ({
        ...prev,
        setupType: { id: setupRaw, name: label },
      }));
      setApiFilter({
        setupType: {
          field: "setupType",
          value: setupRaw,
          comparisonType: "EQUAL",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (resetCalendar) {
      setResetCalendar(false);
    }
  }, [resetCalendar]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMore &&
          !isLoadingEvents &&
          !isLoadingMore &&
          !loadingRef.current
        ) {
          void loadNextBatch(false);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingEvents, isLoadingMore, loadNextBatch, data.length]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .tabs-container::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const getSelectedFiltersText = () => {
    const parts = [];
    parts.push(
      <span key="eventType">
        <span className="font-bold text-sm lg:text-base md:text-sm leading-5">
          Event Type:
        </span>{" "}
        {filters.eventType ? filters.eventType.name : "All Event Types"}
      </span>,
    );
    parts.push(
      <span key="setupType">
        <span className="font-bold text-sm lg:text-base md:text-sm leading-5">
          Setup Type:
        </span>{" "}
        {filters.setupType ? filters.setupType.name : "All"}
      </span>,
    );
    parts.push(
      <span key="year">
        <span className="font-bold text-sm lg:text-base md:text-sm leading-5">
          Year:
        </span>{" "}
        {filters.year
          ? filters.year.name
          : activeTab === "PAST EVENTS"
            ? "All Past"
            : "Event Year"}
      </span>,
    );
    parts.push(
      <span key="region">
        <span className="font-bold text-sm lg:text-base md:text-sm leading-5">
          Region:
        </span>{" "}
        {filters.region ? filters.region.name : "All Regions"}
      </span>,
    );
    parts.push(
      <span key="country">
        <span className="font-bold text-sm lg:text-base md:text-sm leading-5">
          Country:
        </span>{" "}
        {filters.country ? filters.country.name : "All Countries"}
      </span>,
    );
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && (
          <span className="hidden md:inline mx-3">•</span>
        )}
      </React.Fragment>
    ));
  };


  return (
    <div className="bg-white w-full px-12 py-11 flex flex-col">
      <div ref={printRef} className="print-content">
        <div className="flex flex-col xl:flex-row xl:items-start gap-8 flex-1">
          <div className="w-full xl:w-[75%]">
            <div className="bg-white text-left no-print">
              <h2 className="flex items-center flex-row flex-wrap text-white font-sans not-italic font-normal text-xl md:text-2xl leading-5 uppercase py-4 px-5 md:px-6 bg-primary mb-12">
                Search
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5 items-start">
                <div className="flex flex-col">
                  <Input
                    title=""
                    placeholder="Keyword Search"
                    value={filters.keyword}
                    onChange={(e) =>
                      handleFilterChange("keyword", e.target.value)
                    }
                    error={false}
                  />
                </div>

                <div className="flex flex-col">
                  <Dropdown
                    title=""
                    placeholder="All Event Types"
                    data={eventTypeOptions}
                    selectedObjst={filters.eventType?.name}
                    seter={(value) => handleFilterChange("eventType", value)}
                    getid
                    internalSearch
                    maxHeight="max-h-120"
                  />
                </div>

                <div className="flex flex-col">
                  <Dropdown
                    title=""
                    placeholder={
                      activeTab === "PAST EVENTS" ? "Year" : "Event Year"
                    }
                    data={yearOptions}
                    selectedObjst={filters.year?.name}
                    seter={(value) => handleFilterChange("year", value)}
                    getid
                    internalSearch
                    maxHeight="max-h-120"
                    disableSort={true}
                  />
                </div>

                <div className="flex flex-col">
                  <Dropdown
                    title=""
                    placeholder="All Regions"
                    data={regionOptions}
                    selectedObjst={filters.region?.name}
                    seter={(value) => handleFilterChange("region", value)}
                    getid
                    internalSearch
                    maxHeight="max-h-120"
                  />
                </div>

                <div className="flex flex-col">
                  <Dropdown
                    title=""
                    placeholder="All Countries"
                    data={countryOptions}
                    selectedObjst={filters.country?.name}
                    seter={(value) => handleFilterChange("country", value)}
                    getid
                    internalSearch
                    maxHeight="max-h-120"
                  />
                </div>

                <div className="flex items-end gap-2">
                  <NewButton
                    text="SEARCH"
                    onClick={handleSearch}
                    color="primary"
                    size="large"
                    content="textIcon"
                    icon={<ChevronRight className="ml-2 w-6 h-6 fill-white" />}
                    className="!justify-between"
                  />
                </div>
              </div>

              <div className="border-t border-b pt-5 pb-7 print-filters no-print">
                <div className="text-primary font-normal text-sm lg:text-base md:text-sm leading-5">
                  <div className="flex flex-col md:flex-row md:items-start">
                    <div className="flex items-center flex-shrink-0 mb-2 md:mb-0">
                      <span className="font-semibold whitespace-nowrap">
                        Selected Filters
                      </span>
                      <div className="hidden md:block w-[1px] h-4 bg-gray-400 mx-3"></div>
                    </div>
                    <div className="text-gray-font flex flex-col md:flex-row md:flex-wrap md:items-center gap-y-1 md:gap-y-0">
                      {getSelectedFiltersText()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg pt-5 no-print">
              <div className="tabs-container flex flex-wrap md:flex-nowrap items-end border-b-2 border-gray-400 gap-1 overflow-x-auto md:overflow-x-visible">
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    onClick={() => handleTabChange(tab.label)}
                    className={`pb-[10px] pt-[12px] pr-[12px] md:pr-[10px] pl-[10px] md:pl-[10px] border-b-2 font-medium font-decimal text-xs md:text-[11px] lg:text-xs whitespace-nowrap flex-shrink w-full md:w-auto ${
                      tab.label === activeTab
                        ? "border-b-2 text-white bg-[#474F56]"
                        : "border-transparent text-white bg-[#999999]"
                    }`}
                  >
                    <div className="flex items-center gap-1 md:gap-1.5">
                      {tab.label}
                      {tab.label === activeTab ? (
                        <ChevronDown
                          className="fill-white"
                          style={{ width: "12px", height: "12px" }}
                        />
                      ) : (
                        <ChevronDown
                          className="fill-white"
                          rotation="rotate(-90deg)"
                          style={{ width: "12px", height: "12px" }}
                        />
                      )}
                    </div>
                  </button>
                ))}

                <div className="flex flex-row gap-2 md:gap-3 items-center justify-start py-[10px] w-full md:w-auto md:ml-auto flex-shrink-0">
                  <div className="flex items-center gap-2 md:gap-3">
                    <span className="text-primary not-italic font-semibold lg:leading-6 text-base md:leading-5 leading-[18px]">
                      Sort By
                    </span>
                    <span className="text-gray-font font-normal">|</span>
                  </div>
                  <div
                    className="flex items-center gap-2 md:gap-3 cursor-pointer"
                    onClick={() =>
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                    }
                  >
                    <span className="text-gray-font not-italic font-light lg:leading-6 text-base md:leading-5 leading-[18px] relative no-underline bg-transparent after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.1rem] after:bg-primary after:z-[1] after:origin-right after:scale-x-0 after:transition-transform after:duration-[400ms] after:ease-out hover:after:scale-x-100">
                      Date
                    </span>
                    <SortArrow
                      className={`flex w-6 h-6 items-center justify-center fill-gray-font ${sortOrder === "desc" && "rotate-180"}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-10">
              {isLoadingEvents && data.length === 0 ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-primary font-semibold">Loading events...</div>
                </div>
              ) : data.length > 0 ? (
                <>
                  {data.map((event: IEvent, index) => (
                    <div
                      key={event.id}
                      id={`event-card-${event.id}`}
                      className={`event-card-print ${index !== 0 ? "pt-10" : ""}`}
                    >
                      <EventCard
                        event={event}
                        onDetailsClick={handleEventDetails}
                        firmData={firmData}
                        groupData={groupData}
                      />
                    </div>
                  ))}
                  <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
                  <div className="relative flex items-center justify-between w-full no-print pt-4 min-h-[3rem]">
                    <div className="flex flex-1 justify-start items-center">
                      {!isLoadingMore && hasMore && (
                        <button
                          type="button"
                          onClick={() => void loadNextBatch(false)}
                          className="text-red-600 hover:underline font-semibold text-base py-4 cursor-pointer bg-transparent border-0"
                        >
                          Load More
                        </button>
                      )}
                    </div>
                    {isLoadingMore && (
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                        <span className="text-primary font-semibold text-base">
                          Loading...
                        </span>
                      </div>
                    )}
                    <div className="flex flex-1 justify-end items-center">
                      <p className="text-lg text-Default font-semibold py-4 shrink-0">
                        {data.length} of {totalForDisplay} events
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center py-8">
                  <div className="text-gray-500">
                    Check back soon for upcoming events.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="w-full xl:w-[25%] space-y-6 xl:max-w-none mx-auto xl:mx-0 xl:order-2 order-1 no-print">
            <div className="bg-[#F6F6F6] shadow-sm">
              <div className="bg-[#494F56] text-white px-8 py-[22px]">
                <h3 className="text-lg font-normal leading-6">
                  EVENT CALENDAR
                </h3>
              </div>

              <div className="px-[19px] py-5">
                <EventCalendar
                  onDateSelect={(date) => {
                    const eventsOnDate = data?.filter((event) => {
                      if (typeof event.startDate !== "number") {
                        return false;
                      }
                      const eventDate = new Date(event.startDate * 1000);
                      return eventDate.toDateString() === date.toDateString();
                    });

                    if (eventsOnDate && eventsOnDate.length > 0) {
                      const targetEvent = eventsOnDate[0];

                      const eventCardElement = document.getElementById(
                        `event-card-${targetEvent.id}`,
                      );

                      if (eventCardElement) {
                        eventCardElement.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }
                    }
                  }}
                  events={
                    data
                      ?.filter(
                        (event): event is IEvent & { startDate: number } =>
                          typeof event.startDate === "number",
                      )
                      .map((event) => ({
                        date: new Date(event.startDate * 1000),
                        type:
                          event.eventHostSponsor === "wsg" ? "wsg" : "industry",
                        eventId: event.id,
                      })) || []
                  }
                  resetToToday={resetCalendar}
                />
              </div>

              <div className="flex gap-4 px-[19px] pb-5 justify-center lg:justify-start">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                  <span className="text-base leading-[18px] text-gray-font">
                    {" "}
                    WSG Event
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-base leading-[18px] text-gray-font">
                    {" "}
                    Industry Event
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-[#F6F6F6] shadow-sm hidden">
              <div className="p-2">
                <LegacyButton
                  text="SUBMIT AN EVENT"
                  onClick={() => navigate("/submit-event")}
                  color="accent"
                  size="large"
                  content="textIcon"
                  iconOrder="textFirst"
                  icon={<ChevronRight className="w-5 h-5" />}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex mt-10 no-print">
          <div className="flex space-x-4">
            <div className="w-6 h-6 bg-primary rounded-full"></div>
            <div className="w-6 h-6 bg-primary rounded-full"></div>
            <div className="w-6 h-6 bg-primary rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
